import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiCalendar, FiSearch, FiX } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import apiClient from '../utils/apiClient';
import './NewspaperArchive.css';

const formatDate = (date) => {
  if (!date) return '';
  const [year, month, day] = date.split('-');
  return `${year}年${Number(month)}月${Number(day)}日`;
};

const parseKeywords = (keywords) => {
  if (Array.isArray(keywords)) return keywords;
  try {
    return JSON.parse(keywords || '[]');
  } catch {
    return [];
  }
};

const ArticleBody = ({ content }) => (
  <div className="newspaper-reader-copy">
    {(content || '').split(/\n{2,}/).filter(Boolean).map((block, index) => {
      const text = block.trim();
      if (/^\*\*[\s\S]+\*\*$/.test(text)) {
        return <h3 key={index}>{text.replace(/^\*\*|\*\*$/g, '')}</h3>;
      }
      if (/^\*[\s\S]+\*$/.test(text)) {
        return <p className="newspaper-reader-byline" key={index}>{text.replace(/^\*|\*$/g, '')}</p>;
      }
      return <p key={index}>{text.replace(/\*\*/g, '')}</p>;
    })}
  </div>
);

const NewspaperArchive = () => {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [metadata, setMetadata] = useState({ total: 0, timeline: [] });
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [year, setYear] = useState(null);
  const [decade, setDecade] = useState(null);
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [readerLoading, setReaderLoading] = useState(false);
  const searchInputRef = useRef(null);
  const selectedId = articleId || searchParams.get('article');

  useEffect(() => {
    apiClient.get('/newspaper-reports/metadata')
      .then(({ data }) => setMetadata(data))
      .catch(() => setError('档案概览暂时无法载入，请稍后再试。'));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchDraft.trim());
      setPage(1);
    }, 320);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    apiClient.get('/newspaper-reports', {
      params: {
        q: search || undefined,
        year: year || undefined,
        decade: decade || undefined,
        order,
        page,
        pageSize: 12
      }
    }).then(({ data }) => {
      if (!active) return;
      setReports((current) => page === 1 ? data.items : [...current, ...data.items]);
      setTotal(data.total);
    }).catch(() => {
      if (active) setError('报道列表暂时无法载入，请稍后再试。');
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [search, year, decade, order, page]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedReport(null);
      return;
    }

    let active = true;
    setSelectedReport(null);
    setReaderLoading(true);
    apiClient.get(`/newspaper-reports/${selectedId}`)
      .then(({ data }) => {
        if (active) setSelectedReport(data);
      })
      .catch(() => {
        if (active) setSelectedReport(null);
      })
      .finally(() => {
        if (active) setReaderLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedId]);

  const closeReader = useCallback(() => {
    if (articleId) {
      navigate('/newspaper-archive', { replace: true });
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.delete('article');
    setSearchParams(params, { replace: true });
  }, [articleId, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeReader();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedId, closeReader]);

  const years = useMemo(() => metadata.timeline || [], [metadata.timeline]);
  const minYear = years.length ? years[0].year : new Date().getFullYear();
  const maxYear = years.length ? years[years.length - 1].year : minYear;
  const yearRange = Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index);
  const countByYear = useMemo(
    () => Object.fromEntries(years.map((item) => [item.year, item.count])),
    [years]
  );
  const decades = useMemo(
    () => [...new Set(years.map((item) => Math.floor(item.year / 10) * 10))],
    [years]
  );

  const chooseYear = (nextYear) => {
    setYear(year === nextYear ? null : nextYear);
    setDecade(null);
    setPage(1);
  };

  const chooseDecade = (nextDecade) => {
    setDecade(decade === nextDecade ? null : nextDecade);
    setYear(null);
    setPage(1);
  };

  const openReader = (id) => {
    navigate(`/newspaper-archive/article/${id}`);
  };

  const moveReader = (direction) => {
    const currentIndex = reports.findIndex((report) => String(report.id) === String(selectedId));
    const next = reports[currentIndex + direction];
    if (next) openReader(next.id);
  };

  const selectedIndex = reports.findIndex((report) => String(report.id) === String(selectedId));
  const hasMore = reports.length < total;

  return (
    <main className={`newspaper-archive ${selectedId ? 'reader-active' : ''}`}>
      <Navbar />

      <header className="newspaper-hero">
        <div className="newspaper-hero-rule" />
        <h1>报纸档案</h1>
        <p className="newspaper-intro">
          收录报纸中电子游戏的历史报道，以可检索的时间轴呈现电子游戏如何进入社会视野、公共讨论与日常生活。首批馆藏为人民日报历史报道，档案将持续增补。
        </p>
      </header>

      <section className="newspaper-explorer" aria-label="档案时间与检索">
        <div className="newspaper-toolbar">
          <label className="newspaper-search">
            <FiSearch aria-hidden="true" />
            <input
              ref={searchInputRef}
              type="search"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="检索标题、作者或报道全文"
              aria-label="检索报纸档案"
            />
            {searchDraft && (
              <button type="button" onClick={() => setSearchDraft('')} aria-label="清空检索">
                <FiX />
              </button>
            )}
          </label>
          <button
            type="button"
            className="newspaper-order"
            onClick={() => {
              setOrder((current) => current === 'asc' ? 'desc' : 'asc');
              setPage(1);
            }}
          >
            <FiCalendar aria-hidden="true" />
            {order === 'asc' ? '从早到晚' : '从晚到早'}
          </button>
        </div>

        <div className="newspaper-decade-row">
          <button
            type="button"
            className={!year && !decade ? 'active' : ''}
            onClick={() => {
              setYear(null);
              setDecade(null);
              setPage(1);
            }}
          >
            全部年代
          </button>
          {decades.map((item) => (
            <button
              type="button"
              key={item}
              className={decade === item ? 'active' : ''}
              onClick={() => chooseDecade(item)}
            >
              {item}年代
            </button>
          ))}
        </div>

        <div className="newspaper-timeline-wrap">
          <div
            className="newspaper-timeline"
            style={{ minWidth: `${Math.max(720, yearRange.length * 64)}px` }}
          >
            <div className="newspaper-timeline-line" />
            {yearRange.map((item) => {
              const count = countByYear[item] || 0;
              return (
                <button
                  type="button"
                  key={item}
                  disabled={!count}
                  className={`${count ? 'has-reports' : ''} ${year === item ? 'active' : ''}`}
                  onClick={() => chooseYear(item)}
                  aria-label={count ? `${item}年，${count}篇报道` : `${item}年，无报道`}
                >
                  <span className="newspaper-year-count">{count || ''}</span>
                  <span className="newspaper-year-dot" />
                  <span className="newspaper-year-label">{item}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="newspaper-results">
        <div className="newspaper-results-heading">
          <p>
            {search && <>“{search}” · </>}
            {year && <>{year}年 · </>}
            {decade && <>{decade}年代 · </>}
            找到 {total} 篇报道
          </p>
          {(search || year || decade) && (
            <button type="button" onClick={() => {
              setSearchDraft('');
              setSearch('');
              setYear(null);
              setDecade(null);
              setPage(1);
            }}>
              清除筛选
            </button>
          )}
        </div>

        {error && <div className="newspaper-state">{error}</div>}
        {!error && !loading && reports.length === 0 && (
          <div className="newspaper-state">没有找到相符的报道，试试更宽泛的关键词。</div>
        )}

        <div className="newspaper-list">
          {reports.map((report) => (
            <article className={`newspaper-card ${String(report.id) === String(selectedId) ? 'active' : ''}`} key={report.id}>
              <div className="newspaper-card-date">
                <strong>{report.published_date.slice(0, 4)}</strong>
                <span>{Number(report.published_date.slice(5, 7))}月{Number(report.published_date.slice(8, 10))}日</span>
              </div>
              <button
                type="button"
                className="newspaper-card-main"
                onClick={() => openReader(report.id)}
                aria-pressed={String(report.id) === String(selectedId)}
              >
                <div className="newspaper-card-meta">
                  <span>{report.publication}</span>
                  {report.edition && <span>{report.edition}</span>}
                  {report.section_label && <span>{report.section_label}</span>}
                </div>
                <h2>{report.title}</h2>
                <p>{report.excerpt}</p>
                <div className="newspaper-card-footer">
                  <div className="newspaper-tags">
                    {parseKeywords(report.keywords).slice(0, 3).map((keyword) => (
                      <span key={keyword}>{keyword}</span>
                    ))}
                  </div>
                  <span className="newspaper-read-more">阅读全文 <FiArrowRight /></span>
                </div>
              </button>
            </article>
          ))}
        </div>

        {loading && <div className="newspaper-state">正在翻阅馆藏……</div>}
        {hasMore && !loading && (
          <button type="button" className="newspaper-load-more" onClick={() => setPage((current) => current + 1)}>
            继续浏览
          </button>
        )}
      </section>

      {selectedId && (
        <div className="newspaper-reader-layer" role="presentation">
          <aside
            className="newspaper-reader"
            aria-label={selectedReport?.title || '报道全文'}
          >
            <div className="newspaper-reader-topbar">
              <button type="button" onClick={closeReader} aria-label="关闭全文"><FiX /></button>
              <span>{selectedReport ? `${selectedReport.publication} · ${formatDate(selectedReport.published_date)}` : '读取馆藏中'}</span>
              <div>
                <button type="button" disabled={selectedIndex <= 0} onClick={() => moveReader(-1)} aria-label="上一篇"><FiArrowLeft /></button>
                <button type="button" disabled={selectedIndex < 0 || selectedIndex >= reports.length - 1} onClick={() => moveReader(1)} aria-label="下一篇"><FiArrowRight /></button>
              </div>
            </div>

            <div className="newspaper-reader-document">
              {readerLoading && <div className="newspaper-reader-state">正在展开报纸……</div>}
              {!readerLoading && !selectedReport && <div className="newspaper-reader-state">未能找到这篇报道。</div>}
              {selectedReport && (
                <article className="newspaper-reader-article">
                  <p className="newspaper-reader-source">
                    {selectedReport.publication}
                    {selectedReport.edition ? ` · ${selectedReport.edition}` : ''}
                    {selectedReport.section_label ? ` · ${selectedReport.section_label}` : ''}
                  </p>
                  <h2>{selectedReport.title}</h2>
                  <time dateTime={selectedReport.published_date}>{formatDate(selectedReport.published_date)}</time>
                  <div className="newspaper-reader-divider"><span>RGM ARCHIVE</span></div>
                  <ArticleBody content={selectedReport.content} />
                </article>
              )}
            </div>
          </aside>
        </div>
      )}
    </main>
  );
};

export default NewspaperArchive;
